import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus } from 'react-icons/fi';
import DriveItemIcon from './DriveItemIcon';
import { useStorageBox } from '../StorageBoxContext';
import StorageBoxItemMetaBadge from './StorageBoxItemMetaBadge';
import { StorageBoxSidebarTreeSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import { useStorageBoxSidebarDrawer } from '../StorageBoxSidebarDrawerContext';

function TreeNode({ node, depth, treeVersion, expandedFolderIds }) {
  const {
    parentId,
    selected,
    setSelected,
    navigateTo,
    loadChildrenForTree,
    openRenameModal,
    openCreateFolderModal,
    readOnly,
    writable,
  } = useStorageBox();
  const closeDrawer = useStorageBoxSidebarDrawer();

  const isFolder = node.type === 'folder';
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastTreeVersionRef = useRef(treeVersion);

  const isActiveFolder = isFolder && parentId === node.itemId;
  const isOnPath = isFolder && expandedFolderIds.has(node.itemId);
  const isActiveFile = !isFolder && selected?.itemId === node.itemId;
  const canRename = !readOnly && writable && node.canManage;
  const canAddSubfolder = isFolder && canRename;

  const fetchChildren = useCallback(async () => {
    if (!isFolder) return;
    setLoading(true);
    try {
      const items = await loadChildrenForTree(node.itemId);
      setChildren(items);
    } catch {
      setChildren([]);
    } finally {
      setLoading(false);
    }
  }, [isFolder, loadChildrenForTree, node.itemId]);

  const toggle = useCallback(async () => {
    if (!isFolder) return;
    if (!open && !children.length) {
      await fetchChildren();
    }
    setOpen((v) => !v);
  }, [isFolder, open, children.length, fetchChildren]);

  useEffect(() => {
    if (isFolder && isOnPath && !open) {
      fetchChildren().then(() => setOpen(true));
    }
  }, [isOnPath, isFolder, node.itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !isFolder) return;
    if (lastTreeVersionRef.current === treeVersion) return;
    lastTreeVersionRef.current = treeVersion;
    fetchChildren();
  }, [treeVersion, open, isFolder, fetchChildren]);

  const onLabelClick = () => {
    if (isFolder) navigateTo(node.itemId, node);
    else setSelected(node);
    closeDrawer();
  };

  return (
    <li className="sb-tree__node">
      <div
        className={`sb-tree__row ${isActiveFolder || isActiveFile ? 'sb-tree__row--active' : ''}`}
        style={{ paddingLeft: `${depth * 0.65}rem` }}
      >
        {isFolder ? (
          <button type="button" className="sb-tree__toggle" onClick={toggle} aria-label={open ? 'Collapse' : 'Expand'}>
            {loading ? (
              <span className="sb-tree__toggle-skeleton" aria-hidden />
            ) : open ? (
              <FiChevronDown />
            ) : (
              <FiChevronRight />
            )}
          </button>
        ) : (
          <span className="sb-tree__toggle sb-tree__toggle--spacer" aria-hidden />
        )}
        <div className="sb-tree__label-area">
          <button type="button" className="sb-tree__label" onClick={onLabelClick}>
            <span className="sb-tree__icon-wrap">
              <DriveItemIcon item={node} className="sb-tree-list-icon" />
            </span>
            <span className="sb-tree__label-text" title={node.name}>
              {node.name}
            </span>
          </button>
          <span className="sb-tree__meta-slot">
            <StorageBoxItemMetaBadge item={node} variant="tree" />
          </span>
        </div>
        {canAddSubfolder ? (
          <button
            type="button"
            className="sb-tree__add-folder"
            aria-label={`New subfolder in ${node.name}`}
            title={`New subfolder in ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              openCreateFolderModal(node.itemId);
              closeDrawer();
            }}
          >
            <FiPlus />
          </button>
        ) : null}
        {canRename ? (
          <button
            type="button"
            className="sb-tree__rename"
            aria-label={`Rename ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              openRenameModal(node);
              closeDrawer();
            }}
          >
            <FiEdit2 />
          </button>
        ) : null}
      </div>
      {isFolder && open ? (
        <ul className="sb-tree__children">
          {children.length
            ? children.map((c) => (
                <TreeNode
                  key={`${c.itemId}-${c.folderColor || ''}-${c.colorTag || ''}`}
                  node={c}
                  depth={depth + 1}
                  treeVersion={treeVersion}
                  expandedFolderIds={expandedFolderIds}
                />
              ))
            : loading ? (
                <li className="sb-tree__skeleton-wrap">
                  <StorageBoxSidebarTreeSkeleton rows={2} />
                </li>
              ) : (
                <li className="sb-tree__empty">Empty</li>
              )}
        </ul>
      ) : null}
    </li>
  );
}

export default function ItemTree() {
  const { loadChildrenForTree, treeVersion, breadcrumbs } = useStorageBox();

  const expandedFolderIds = useMemo(
    () => new Set((breadcrumbs || []).map((b) => b.itemId)),
    [breadcrumbs]
  );
  const [roots, setRoots] = useState([]);
  const [rootsLoading, setRootsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRootsLoading(true);
    loadChildrenForTree(null)
      .then((items) => {
        if (!cancelled) setRoots(items);
      })
      .catch(() => {
        if (!cancelled) setRoots([]);
      })
      .finally(() => {
        if (!cancelled) setRootsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadChildrenForTree, treeVersion]);

  return (
    <nav className="sb-tree sb-tree--full" aria-label="Files and folders">
      <ul className="sb-tree__list">
        {rootsLoading ? (
          <li className="sb-tree__skeleton-wrap">
            <StorageBoxSidebarTreeSkeleton rows={5} />
          </li>
        ) : !roots.length ? (
          <li className="sb-tree__empty">No items yet</li>
        ) : (
          roots.map((node) => (
            <TreeNode
              key={`${node.itemId}-${node.folderColor || ''}-${node.colorTag || ''}`}
              node={node}
              depth={0}
              treeVersion={treeVersion}
              expandedFolderIds={expandedFolderIds}
            />
          ))
        )}
      </ul>
    </nav>
  );
}
